/*
============================================================
 URLParameterParser - Unified URL parameter handling
------------------------------------------------------------
 Centralized parsing of URL parameters for:
 - Puzzle selection (file or date-based)
 - Origin-aware routing (returnTo)
 - Puzzle persistence (localStorage/sessionStorage)
============================================================
*/

/**
 * Parse and manage URL parameters for puzzle loading
 * @class URLParameterParser
 */
export class URLParameterParser {
  /**
   * Safely set item in storage (handles restricted CI environments)
   * @private
   * @static
   * @param {Storage} storage - sessionStorage or localStorage
   * @param {string} key - The key to set
   * @param {string} value - The value to store
   */
  static _setStorageItem(storage, key, value) {
    try {
      if (storage && typeof storage.setItem === "function") {
        storage.setItem(key, value);
      }
    } catch {
      // Storage might not be available or writable in test environment
    }
  }

  /**
   * Safely get item from storage (handles restricted CI environments)
   * @private
   * @static
   * @param {Storage} storage - sessionStorage or localStorage
   * @param {string} key - The key to retrieve
   * @returns {string|null} The stored value or null
   */
  static _getStorageItem(storage, key) {
    try {
      if (storage && typeof storage.getItem === "function") {
        return storage.getItem(key);
      }
    } catch {
      // Storage might not be available in test environment
    }
    return null;
  }
  /**
   * Extract and validate puzzle source ID from URL or storage
   * Priority: URL → sessionStorage → localStorage
   * Also identifies the source type (file, date, or sample)
   * @static
   * @returns {{sourceId: string|null, sourceType: string|null}} The validated source ID and type or nulls
   */
  static extractSourceId() {
    const params = new URLSearchParams(window.location.search);
    let sourceId = null;
    let sourceType = null;

    // Check URL parameters (priority: file > date > sample > archive > puzzle)
    if (params.has("file")) {
      sourceId = params.get("file");
      sourceType = "file";
    } else if (params.has("date")) {
      sourceId = params.get("date");
      sourceType = "date";
    } else if (params.has("sample")) {
      sourceId = params.get("sample");
      sourceType = "sample";
    } else if (params.has("archive")) {
      sourceId = params.get("archive");
      sourceType = "archive";
    } else if (params.has("puzzle")) {
      // Legacy parameter name - treat as file
      sourceId = params.get("puzzle");
      sourceType = "file";
    }

    // Save to sessionStorage if found
    if (sourceId && this._isValidSourceId(sourceId)) {
      this._setStorageItem(sessionStorage, "selectedPuzzle", sourceId.trim());
      this._setStorageItem(sessionStorage, "selectedSourceType", sourceType);
      return { sourceId: sourceId.trim(), sourceType };
    }

    // Fall back to sessionStorage
    const sessionPuzzle = this._getStorageItem(
      sessionStorage,
      "selectedPuzzle",
    );
    if (sessionPuzzle && this._isValidSourceId(sessionPuzzle)) {
      const sessionType = this._getStorageItem(
        sessionStorage,
        "selectedSourceType",
      );
      return { sourceId: sessionPuzzle, sourceType: sessionType || "file" };
    }

    // Fall back to localStorage
    const storedPuzzle = this._getStorageItem(localStorage, "selectedPuzzle");
    if (storedPuzzle && this._isValidSourceId(storedPuzzle)) {
      const storedType = this._getStorageItem(
        localStorage,
        "selectedSourceType",
      );
      return { sourceId: storedPuzzle, sourceType: storedType || "file" };
    }

    return { sourceId: null, sourceType: null };
  }

  /**
   * Extract and validate return URL for origin-aware routing
   * Ensures URL is same-origin for security
   * @static
   * @returns {string|null} The validated return URL or null
   */
  static extractReturnUrl() {
    const params = new URLSearchParams(window.location.search);

    if (!params.has("returnTo")) {
      return null;
    }

    const returnToParam = params.get("returnTo");

    try {
      // Validate that URL is same-origin
      const returnUrl = new URL(returnToParam, window.location.origin);

      if (returnUrl.origin !== window.location.origin) {
        console.warn(
          "[URLParameterParser] Rejecting cross-origin returnTo URL",
        );
        return null;
      }

      // Store for later use
      this._setStorageItem(sessionStorage, "returnTo", returnUrl.href);
      console.log(
        "[URLParameterParser] Captured returnTo URL:",
        returnUrl.href,
      );

      return returnUrl.href;
    } catch {
      console.warn("[URLParameterParser] Invalid returnTo URL:", returnToParam);
      return null;
    }
  }

  /**
   * Get stored return URL for logo redirect
   * @static
   * @returns {string|null}
   */
  static getStoredReturnUrl() {
    return this._getStorageItem(sessionStorage, "returnTo");
  }

  /**
   * Validate source identifier format
   * @private
   * @static
   * @param {string} sourceId - Identifier to validate
   * @returns {boolean}
   */
  static _isValidSourceId(sourceId) {
    if (!sourceId) return false;
    // Allow alphanumeric, underscore, dash (filenames and YYYYMMDD dates)
    return /^[0-9A-Za-z_-]+$/.test(sourceId.trim());
  }
}
